import { computed, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { AGGREGATIONS_SIGNALR_HUB, LIVE_SIGNALR_HUB } from '@dxp/core/signalr';
import { environment } from '@dxp/core/environment';
import {
    assignKpiQueueThresholdFromLegacy,
    IKpiQueueThreshold,
    IQueue,
    IQueueAttributeRequirement,
    IQueueInformation,
    IQueueWithChannelsReport,
    ITelXLKpiQueueThreshold,
    IWorkflowInfo,
    mergeMissingQueueThresholds,
    Queue,
    TelXLKpiQueueThresholdDefaultState,
} from '@dxp/core/models';
import {
    signalStore,
    withComputed,
    withFeature,
    withHooks,
    withMethods,
    withState,
} from '@ngrx/signals';
import {
    catchError,
    combineLatest,
    distinctUntilChanged,
    filter,
    from,
    interval,
    map,
    mergeMap,
    of,
    switchMap,
    tap,
    toArray,
} from 'rxjs';
import { updateState } from '@angular-architects/ngrx-toolkit';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { RbacApiService } from '@dxp/core/api';
import { ChannelProviderType } from '@dxp/core/types';
import { ReloadService } from '@dxp/core/services';
import {
    witChannelProviders,
    CHANNELPROVIDER_STORE,
} from '../../channel-provider';
import {
    agentSegmentKey,
    areStringArraysEqualUnordered,
    asDate,
    deriveQueueIdsFromTeams,
    isSameAgentSegment,
    isSameQueueInformationShallow,
    isSameQueueReportShallow,
    isSameQueueSegment,
    mergeByKey,
    mergeChannelReports,
    normalizeQueueInformation,
    queueSegmentKey,
} from './queue.helpers';

const KPI_CONCURRENCY = 5;

interface IQueueStoreState {
    queueIds: string[];
    report: Record<string, IQueueWithChannelsReport>;
    information: Record<string, IQueueInformation>;
    kpi: Record<string, ITelXLKpiQueueThreshold>;
    attribute: Record<string, IQueueAttributeRequirement[]>;
    unableToAssign: string[];
    loading: boolean;
}

const initialState: IQueueStoreState = {
    queueIds: [],
    report: {},
    information: {},
    kpi: {},
    attribute: {},
    unableToAssign: [],
    loading: true,
};

export const QueueStore = signalStore(
    { providedIn: 'root' },
    environment.storeWithDevTools('QueueStore'),
    withFeature(() => witChannelProviders(inject(CHANNELPROVIDER_STORE))),
    withState<IQueueStoreState>(initialState),
    withMethods(store => {
        const tick = signal(0);

        return {
            incrementTick() {
                tick.update(v => v + 1);
            },

            get tick() {
                return tick;
            },

            getChannelProvidersByWorkflowId(
                workflowId: string,
            ): ChannelProviderType[] {
                return store.getChannelProvidersByWorkflowId(workflowId);
            },
        };
    }),
    withComputed(store => ({
        ready: computed<boolean>(() => {
            const infos = store.information();

            return (
                store.loading() === false &&
                store.queueIds().every(id => id in infos)
            );
        }),
        queues: computed<IQueue[]>(() => {
            store.tick();

            const ids = store.queueIds();
            const information = store.information();
            const reports = store.report();
            const thresholds = store.kpi();
            const attributes = store.attribute();

            return ids.map(id => {
                const info = information[id];
                const report = reports[id];
                const threshold =
                    thresholds[id] ?? TelXLKpiQueueThresholdDefaultState;
                const channelProviders = info
                    ? store.getChannelProvidersByWorkflowId(info.workflowId)
                    : [];
                const attributeRequirements = attributes[id] ?? [];
                const unableToAssign = store.unableToAssign();

                return new Queue(
                    id,
                    threshold,
                    unableToAssign,
                    channelProviders,
                    info,
                    report,
                    attributeRequirements,
                );
            });
        }),
    })),
    withMethods(store => ({
        getQueueKpi(queueId: string): ITelXLKpiQueueThreshold {
            return store.kpi()[queueId] ?? TelXLKpiQueueThresholdDefaultState;
        },
        updateQueuesFromTeams(queueIds: string[]) {
            updateState(store, 'UPDATE_QUEUE_ARRAY', state => {
                const prevQueueIds = state.queueIds;

                if (areStringArraysEqualUnordered(prevQueueIds, queueIds)) {
                    return state;
                }

                return {
                    ...state,
                    queueIds: queueIds,
                };
            });
        },
        updateQueueReport(report: IQueueWithChannelsReport) {
            if (report.name === 'Not found') return;

            const normalized: IQueueWithChannelsReport = {
                ...report,
                startOfToday: asDate(report.startOfToday) as Date,
                queueChannelReports: (report.queueChannelReports ?? []).map(
                    r => ({
                        ...r,
                        startOfToday: asDate(r.startOfToday) as Date,
                    }),
                ),
            };

            updateState(store, 'UPDATE_QUEUE_REPORT', state => {
                const prev = state.report[normalized.id];

                if (!prev) {
                    return {
                        ...state,
                        report: {
                            ...state.report,
                            [normalized.id]: normalized,
                        },
                    };
                }

                const shallowSame = isSameQueueReportShallow(prev, normalized);
                const mergedChannels = mergeChannelReports(
                    prev.queueChannelReports,
                    normalized.queueChannelReports,
                );

                if (shallowSame && !mergedChannels.changed) {
                    return state;
                }

                const nextReport: IQueueWithChannelsReport =
                    mergedChannels.changed
                        ? {
                              ...normalized,
                              queueChannelReports: mergedChannels.value,
                          }
                        : {
                              ...normalized,
                              queueChannelReports: prev.queueChannelReports,
                          };

                return {
                    ...state,
                    report: {
                        ...state.report,
                        [normalized.id]: nextReport,
                    },
                };
            });
        },
        updateQueueInformation(information: IQueueInformation) {
            const normalized = normalizeQueueInformation(information);

            updateState(store, 'UPDATE_QUEUE_INFORMATION', state => {
                const prev = state.information[normalized.id];

                if (!prev) {
                    return {
                        ...state,
                        information: {
                            ...state.information,
                            [normalized.id]: normalized,
                        },
                    };
                }

                const shallowSame = isSameQueueInformationShallow(
                    prev,
                    normalized,
                );

                const q1 = mergeByKey(
                    prev.queueSegments,
                    normalized.queueSegments,
                    queueSegmentKey,
                    isSameQueueSegment,
                );
                const a1 = mergeByKey(
                    prev.agentSegments,
                    normalized.agentSegments,
                    agentSegmentKey,
                    isSameAgentSegment,
                );
                const q2 = mergeByKey(
                    prev.todaysQueueSegments,
                    normalized.todaysQueueSegments,
                    queueSegmentKey,
                    isSameQueueSegment,
                );
                const a2 = mergeByKey(
                    prev.todaysAgentSegments,
                    normalized.todaysAgentSegments,
                    agentSegmentKey,
                    isSameAgentSegment,
                );
                const q3 = mergeByKey(
                    prev.segments,
                    normalized.segments,
                    queueSegmentKey,
                    isSameQueueSegment,
                );

                const anyArrayChanged =
                    q1.changed ||
                    a1.changed ||
                    q2.changed ||
                    a2.changed ||
                    q3.changed;

                if (shallowSame && !anyArrayChanged) {
                    return state;
                }

                const nextInfo: IQueueInformation = {
                    ...normalized,
                    queueSegments: q1.changed ? q1.value : prev.queueSegments,
                    agentSegments: a1.changed ? a1.value : prev.agentSegments,
                    todaysQueueSegments: q2.changed
                        ? q2.value
                        : prev.todaysQueueSegments,
                    todaysAgentSegments: a2.changed
                        ? a2.value
                        : prev.todaysAgentSegments,
                    segments: q3.changed ? q3.value : prev.segments,
                };

                return {
                    ...state,
                    information: {
                        ...state.information,
                        [normalized.id]: nextInfo,
                    },
                };
            });
        },
        updateQueueKpi(
            queueId: string,
            kpiThreshold?: IKpiQueueThreshold,
            telXLKpiThreshold?: ITelXLKpiQueueThreshold,
        ) {
            const kpi =
                telXLKpiThreshold ??
                assignKpiQueueThresholdFromLegacy(kpiThreshold);

            mergeMissingQueueThresholds(kpi);

            updateState(store, 'UPDATE_QUEUE_KPI', state => {
                return {
                    ...state,
                    kpi: {
                        ...state.kpi,
                        [queueId]: kpi,
                    },
                    loading: false,
                };
            });
        },
        updateQueueAttributes(
            attributes: Record<string, IQueueAttributeRequirement[]>,
        ) {
            updateState(store, 'UPDATE_QUEUE_ATTRIBUTES', state => {
                return {
                    ...state,
                    attribute: attributes,
                };
            });
        },
        failedToAssignConversation(conversationId: string) {
            updateState(store, 'UPDATE_QUEUE_UNABLE_TO_ASSIGN', state => {
                return {
                    ...state,
                    unableToAssign: [...state.unableToAssign, conversationId],
                };
            });
        },
        removeQueue(queueId: string) {
            updateState(store, 'REMOVE_QUEUE', state => {
                const { [queueId]: _, ...newReport } = state.report;
                const { [queueId]: __, ...newInformation } = state.information;
                const { [queueId]: ___, ...newKpi } = state.kpi;
                const { [queueId]: ____, ...newAttribute } = state.attribute;

                return {
                    ...state,
                    queueIds: state.queueIds.filter(id => id !== queueId),
                    report: newReport,
                    information: newInformation,
                    kpi: newKpi,
                    attribute: newAttribute,
                };
            });
        },
    })),
    withHooks({
        onInit(
            store,
            aggregationsHubService = inject(AGGREGATIONS_SIGNALR_HUB),
            liveHubService = inject(LIVE_SIGNALR_HUB),
            oidcSecurityService = inject(OidcSecurityService),
            rbacApiService = inject(RbacApiService),
            reloadService = inject(ReloadService),
            destroyRef = inject(DestroyRef),
        ) {
            combineLatest(
                oidcSecurityService.userData$,
                toObservable(reloadService.refreshTime),
                toObservable(reloadService.refreshTeams),
            )
                .pipe(
                    map(([user, refresh, refreshTeams]) => ({
                        userId: user?.userData?.sub as unknown,
                        refresh,
                        refreshTeams,
                    })),
                    filter(
                        (
                            x,
                        ): x is {
                            userId: string;
                            refresh: number;
                            refreshTeams: number;
                        } =>
                            typeof x.userId === 'string' && x.userId.length > 0,
                    ),
                    distinctUntilChanged(
                        (a, b) =>
                            a.userId === b.userId &&
                            a.refresh === b.refresh &&
                            a.refreshTeams === b.refreshTeams,
                    ),
                    switchMap(({ userId }) =>
                        rbacApiService.getTeams(userId).pipe(
                            catchError(error => {
                                reloadService.error(
                                    'QUEUE_STORE',
                                    error.message,
                                );

                                return of([]);
                            }),
                        ),
                    ),
                    map(teams => deriveQueueIdsFromTeams(teams)),
                    distinctUntilChanged(areStringArraysEqualUnordered),
                    tap(queueIds => store.updateQueuesFromTeams(queueIds)),
                    switchMap(queueIds => {
                        if (queueIds.length === 0) {
                            reloadService.error(
                                'QUEUE_STORE',
                                'No queues found',
                            );

                            return of(
                                [] as { queueId: string; kpis?: undefined }[],
                            );
                        }

                        return from(queueIds).pipe(
                            mergeMap(
                                queueId =>
                                    rbacApiService
                                        .getKpiThresholds(queueId, 'queues')
                                        .pipe(
                                            catchError(() => of(undefined)),
                                            map(kpis => ({ queueId, kpis })),
                                        ),
                                KPI_CONCURRENCY,
                            ),
                            toArray(),
                        );
                    }),
                    tap(results => {
                        for (const { queueId, kpis } of results) {
                            store.updateQueueKpi(
                                queueId,
                                kpis?.kpiThreshold as
                                    | IKpiQueueThreshold
                                    | undefined,
                                kpis?.kpiTelXLThreshold as
                                    | ITelXLKpiQueueThreshold
                                    | undefined,
                            );
                        }

                        const resultQueueIds = new Set(
                            results.map(r => r.queueId),
                        );
                        const existingQueueIds = store.queueIds();

                        const removedQueueIds = existingQueueIds.filter(
                            queueId => !resultQueueIds.has(queueId),
                        );

                        for (const queueId of removedQueueIds) {
                            store.removeQueue(queueId);
                        }
                    }),
                    takeUntilDestroyed(destroyRef),
                )
                .subscribe();

            oidcSecurityService.isAuthenticated$
                .pipe(
                    map(r => r.isAuthenticated),
                    distinctUntilChanged(),
                    filter(Boolean),
                    switchMap(() =>
                        rbacApiService.getWorkflowDetails().pipe(
                            map((workflows: IWorkflowInfo[]) => {
                                const record: Record<
                                    string,
                                    IQueueAttributeRequirement[]
                                > = {};

                                for (const wf of workflows ?? []) {
                                    for (const q of wf.queues ?? []) {
                                        record[q.queueId] =
                                            q.attributeRequirements ?? [];
                                    }
                                }

                                return record;
                            }),
                            catchError(() => of({})),
                        ),
                    ),
                    tap(attributesByQueueId =>
                        store.updateQueueAttributes(attributesByQueueId),
                    ),
                    takeUntilDestroyed(destroyRef),
                )
                .subscribe();

            aggregationsHubService.queueReport$
                .pipe(
                    tap(report => store.updateQueueReport(report)),
                    takeUntilDestroyed(destroyRef),
                )
                .subscribe();

            liveHubService.queueInformation$
                .pipe(
                    tap(info => store.updateQueueInformation(info)),
                    takeUntilDestroyed(destroyRef),
                )
                .subscribe();

            interval(1000)
                .pipe(takeUntilDestroyed(destroyRef))
                .subscribe(() => store.incrementTick());
        },
    }),
);
